import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@rudi/ui/dropdown-menu';
import { cn } from '@rudi/ui/lib/utils';
import { hasSession } from '../platform/auth-storage';
import {
  fetchTargets,
  readTargetId,
  readTargetLabel,
  switchTarget,
  targetDetails,
  type TargetSummary,
} from '../platform/target';

/** Rudi connections offered by the backend; empty until loaded or when listing fails. */
export function useTargets(): TargetSummary[] {
  const [targets, setTargets] = useState<TargetSummary[]>([]);
  useEffect(() => {
    let active = true;
    fetchTargets()
      .then(list => {
        if (active) setTargets(list);
      })
      .catch(() => {
        // The switcher is optional; Cortex still uses the current connection.
      });
    return () => {
      active = false;
    };
  }, []);
  return targets;
}

/**
 * Picks the Rudi connection. Each entry shows the stack's frontend host, which
 * carries its unique prefix, and marks connections this browser is already
 * signed in to, since switching to those skips the login screen.
 */
export function TargetSwitcher({
  targets,
  id = 'rudi-target',
  className,
}: {
  targets: TargetSummary[];
  id?: string;
  className?: string;
}) {
  const selectedId = readTargetId();
  const standing = targets.find(target => target.kind === 'standing');
  const currentId = selectedId || standing?.id || '';
  const current = targets.find(target => target.id === currentId);
  const title = current?.label || readTargetLabel() || selectedId || 'Rudi';
  const details = current ? targetDetails(current) : '';
  const signedIn = (target: TargetSummary) =>
    hasSession(target.kind === 'standing' ? '' : target.id);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        id={id}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-3xl border border-input bg-background px-3 py-1.5 text-left text-sm',
          current?.kind === 'stack' && 'border-amber-300 bg-amber-50 dark:bg-amber-900/30',
          className
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{title}</span>
          {details && (
            <span className="block truncate text-xs text-muted-foreground">{details}</span>
          )}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[70vh] w-[26rem] max-w-[90vw] overflow-y-auto"
      >
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Rudi instance
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={currentId}
          onValueChange={value => {
            if (value === currentId) return;
            switchTarget(targets.find(target => target.id === value) ?? null);
          }}
        >
          {targets.map(target => (
            <DropdownMenuRadioItem key={target.id} value={target.id} className="items-start">
              <span className="min-w-0 flex-1">
                <span className="block truncate">{target.label}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {targetDetails(target)}
                </span>
              </span>
              {signedIn(target) && (
                <span
                  className="ml-2 mt-0.5 flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
                  title="This browser holds a session for this instance"
                >
                  <Check className="h-3 w-3" />
                  Signed in
                </span>
              )}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
